import {css, StyleSheet} from 'aphrodite';
import classnames from 'classnames';
import React from 'react';

import {ProcessTaskStatusType, ReviewStatus} from 'graphql-client/__generated/graphqlGlobalTypes';
import {getBadgeStatus} from 'notebook/reviews/ReviewBadge';
import {getStatusIcon} from 'notebook/reviews/ReviewUtil';
import {STYLE_FOR_REVIEW_STATUS, Worksheet} from 'procedures/worksheets/WorksheetNavigationComponent';
import TaskStatusIcon from 'processes/status/TaskStatusIcon';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

// the aggregate count of each task status type is specifically ordered by importance for the operators.
const TASK_STATUS_TYPES_DISPLAY_ORDER = [
  ProcessTaskStatusType.IN_PROGRESS,
  ProcessTaskStatusType.COMPLETED,
  ProcessTaskStatusType.SKIPPED,
  ProcessTaskStatusType.FAILED,
  ProcessTaskStatusType.PENDING,
  ProcessTaskStatusType.CANCELLED,
  ProcessTaskStatusType.INVALID,
];

// the aggregate count of each review status type is specifically ordered by importance for the operators.
const REVIEW_STATUS_TYPES_DISPLAY_ORDER = [
  ReviewStatus.ACTION_REQUIRED,
  ReviewStatus.REJECTED,
  ReviewStatus.REVIEW_SNAPSHOT_IN_PROGRESS,
  ReviewStatus.NEEDS_REVIEW,
  ReviewStatus.ACCEPTANCE_SNAPSHOT_IN_PROGRESS,
  ReviewStatus.ACCEPTED,
  ReviewStatus.RETRACTED,
];

const PROCESS_TASK_STATUS_TYPE_TO_STRING: {[key in ProcessTaskStatusType]: string} = {
  [ProcessTaskStatusType.CANCELLED]: 'Cancelled',
  [ProcessTaskStatusType.COMPLETED]: 'Completed',
  [ProcessTaskStatusType.FAILED]: 'Failed',
  [ProcessTaskStatusType.INVALID]: 'Invalid',
  [ProcessTaskStatusType.IN_PROGRESS]: 'In Progress',
  [ProcessTaskStatusType.PENDING]: 'Pending',
  [ProcessTaskStatusType.SKIPPED]: 'Skipped',
};

function TaskStatusElement({status, text}: {status: ProcessTaskStatusType; text: string}): JSX.Element {
  return (
    <>
      <Tooltip text={PROCESS_TASK_STATUS_TYPE_TO_STRING[status]} placement={FloatingPlacement.TOP}>
        <TaskStatusIcon type={status} />
      </Tooltip>
      <div className={css(styles.taskStatusNum)}>{text}</div>
    </>
  );
}

const REVIEW_STATUS_TYPE_TO_STRING: {[key in ReviewStatus]: string} = {
  [ReviewStatus.ACCEPTANCE_SNAPSHOT_IN_PROGRESS]: 'Accepted',
  [ReviewStatus.ACCEPTED]: 'Accepted',
  [ReviewStatus.ACTION_REQUIRED]: 'Action required',
  [ReviewStatus.NEEDS_REVIEW]: 'Needs review',
  [ReviewStatus.REJECTED]: 'Rejected',
  [ReviewStatus.RETRACTED]: 'Retracted',
  [ReviewStatus.REVIEW_SNAPSHOT_IN_PROGRESS]: 'Needs review',
};

function ReviewStatusElement({status, text}: {status: ReviewStatus; text: string}): JSX.Element {
  const badgeStatus = getBadgeStatus(status);
  const style = STYLE_FOR_REVIEW_STATUS[badgeStatus];
  const icon = getStatusIcon(status);
  return (
    <>
      <Tooltip text={REVIEW_STATUS_TYPE_TO_STRING[status]} placement={FloatingPlacement.TOP}>
        <BenchlingIcon className={classnames(css(styles.reviewStatusIcon, style))} icon={icon} />
      </Tooltip>
      <div className={css(styles.taskStatusNum)}>{text}</div>
    </>
  );
}

function getSingleTaskStatusSummary(
  status: ProcessTaskStatusType,
  taskStatusCount: {[key in ProcessTaskStatusType]: number}
): string {
  return `${taskStatusCount[status]}`;
}

function getSingleReviewStatusSummary(
  status: ReviewStatus,
  reviewStatusCount: {[key in ReviewStatus]: number}
): string {
  return `${reviewStatusCount[status]}`;
}

export default function TaskStatusSummaryHeader({worksheets}: {worksheets: Array<Worksheet>}): JSX.Element {
  const taskStatusCount: {[key in ProcessTaskStatusType]: number} = {
    CANCELLED: 0,
    COMPLETED: 0,
    FAILED: 0,
    INVALID: 0,
    IN_PROGRESS: 0,
    PENDING: 0,
    SKIPPED: 0,
  };
  const reviewStatusCount: {[key in ReviewStatus]: number} = {
    ACCEPTANCE_SNAPSHOT_IN_PROGRESS: 0,
    ACCEPTED: 0,
    ACTION_REQUIRED: 0,
    NEEDS_REVIEW: 0,
    REJECTED: 0,
    RETRACTED: 0,
    REVIEW_SNAPSHOT_IN_PROGRESS: 0,
  };
  let totalInReviewCount = 0;
  for (const worksheet of worksheets) {
    if (worksheet.worksheetReviewStatus && worksheet.worksheetReviewStatus !== ReviewStatus.RETRACTED) {
      reviewStatusCount[worksheet.worksheetReviewStatus] += 1;
      totalInReviewCount += 1;
    } else {
      taskStatusCount[worksheet.taskStatus] += 1;
    }
  }

  // if there's only one status to display, we'll add the status name to its count
  const onlyOneStatus =
    Object.values(taskStatusCount).filter((value) => value !== 0).length +
      Object.values(reviewStatusCount).filter((value) => value !== 0).length ===
    1;

  function renderTaskStatusElement(status: ProcessTaskStatusType): JSX.Element {
    return (
      <TaskStatusElement
        key={`${PROCESS_TASK_STATUS_TYPE_TO_STRING[status].toUpperCase()}-${taskStatusCount[status]}`}
        status={status}
        text={
          onlyOneStatus
            ? getSingleTaskStatusSummary(status, taskStatusCount)
            : taskStatusCount[status].toString()
        }
      />
    );
  }

  function renderReviewStatusElement(status: ReviewStatus): JSX.Element {
    return (
      <ReviewStatusElement
        key={`${status}_${reviewStatusCount[status]}`}
        status={status}
        text={
          onlyOneStatus
            ? getSingleReviewStatusSummary(status, reviewStatusCount)
            : reviewStatusCount[status].toString()
        }
      />
    );
  }

  return (
    <div>
      <div className={css(styles.taskStatusSummary)}>
        {TASK_STATUS_TYPES_DISPLAY_ORDER.map((status) => {
          if (taskStatusCount[status]) {
            return renderTaskStatusElement(status);
          }
          return null;
        })}
      </div>
      {totalInReviewCount > 0 && (
        <div id="task-status-review-section">
          <div>In Review</div>
          <div className={css(styles.taskStatusSummary)}>
            {REVIEW_STATUS_TYPES_DISPLAY_ORDER.map((status) => {
              if (reviewStatusCount[status]) {
                return renderReviewStatusElement(status);
              }
              return null;
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export const styles = StyleSheet.create({
  taskStatusSummary: {
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'row',
    paddingTop: '5px',
    paddingBottom: '5px',
  },
  taskStatusNum: {
    paddingRight: '10px',
  },
  reviewStatusIcon: {
    height: 14,
    width: 14,
    marginRight: '6px',
  },
});
